import { useState } from 'react';
import PropTypes from 'prop-types';
import * as Portal from '@radix-ui/react-portal';
import MobileSheet from './MobileSheet';

/**
 * ResultsMobileActionBar (TD-2576 / TD-2762)
 *
 * Shared mobile bottom action bar for the Profiler results surfaces — Search
 * results, saved-project results and Team feedback. A fixed bar **portaled to
 * `<body>`** (Radix `Portal.Root`) so it lives in the browser's top layer and
 * cannot be trapped by a transformed / scrolled ancestor — the class of bug
 * fixed for the Add-filter drawer (TD-2756) and already solved by the enrichment
 * bar (`.key-contacts-sticky-bar`, TD-2485), which this generalises.
 *
 * The bar takes **descriptors**, never nodes: up to `actions` inline as icon+label
 * buttons, one emphasized `primary` CTA, and — when `moreActions` is non-empty — a
 * "More (⋯)" bottom sheet (shared `MobileSheet`). It renders them as real
 * `<button>`s, so every control is focusable and has an accessible name.
 *
 * There was a `children` slot mode (TD-2763) in which a surface passed its existing
 * control nodes instead. It was removed in TD-2769: because the bar portals to
 * `<body>`, anything handed to it left its page wrapper behind, and every control
 * whose styling was written under one rendered unstyled — the saved-project
 * Customize sheet and the Team view/sort toggles both shipped broken that way. Any
 * menu the bar opens belongs in a `MobileSheet` rendered as a **sibling** of the
 * bar: a native `<dialog>` is promoted to the top layer without leaving its place
 * in the DOM, so it keeps its ancestors and its styling.
 *
 * `breakpoint` hides the bar (and its in-flow spacer) on wider viewports via a
 * CSS media query, so each surface keeps its own switch point (`to-1200` for
 * Projects/Team, `to-833` for Search) while the element stays in the DOM — the
 * same CSS-gated model the surfaces used before. Labels drop to icon-only under
 * ~360px. The spacer keeps the last results row clear of the fixed bar.
 *
 * Presentational only: consumers (MobX observers) pass handlers/state in. Icons
 * are image `src` strings (e.g. `ICON_BUTTON_ICONS.*`).
 */
const actionShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  active: PropTypes.bool,
  /** Something the action started is still running: highlighted like `active`,
      exposed as aria-busy and a label suffix rather than as a pressed toggle. */
  busy: PropTypes.bool
});

// The accessible name of a busy control; starts with the visible label so
// Label-in-Name still holds.
const accessibleLabel = (label, busy) => (busy ? `${label}, in progress` : label);

const ResultsMobileActionBar = ({
  actions = [],
  primary = null,
  moreActions = [],
  moreBusy = false,
  moreLabel = 'More',
  sheetTitle = 'More actions',
  ariaLabel = 'Results actions',
  breakpoint = null
}) => {
  const [isSheetOpen, setSheetOpen] = useState(false);

  const hasMore = moreActions.length > 0;
  const bpBar = breakpoint ? ` results-mobile-action-bar--${breakpoint}` : '';
  const bpSpacer = breakpoint ? ` results-mobile-action-bar-spacer--${breakpoint}` : '';
  const spacer = (
    <div className={`results-mobile-action-bar-spacer${bpSpacer}`} aria-hidden="true" />
  );

  // aria-label duplicates the visible <small> on purpose: the 360px rule hides
  // that label with `display: none`, which drops it from the accessibility tree,
  // and the icon is aria-hidden — so without the attribute these become unnamed
  // buttons on exactly the narrowest screens. Same string, so Label-in-Name holds.
  const renderBarButton = (action) => (
    <button
      key={action.key}
      type="button"
      className={`rmab-btn${action.active || action.busy ? ' is-active' : ''}`}
      onClick={action.onClick}
      disabled={action.disabled}
      aria-pressed={action.active || undefined}
      aria-busy={action.busy || undefined}
      aria-label={accessibleLabel(action.label, action.busy)}
    >
      {action.icon ? (
        <img src={action.icon} alt="" aria-hidden="true" className="rmab-btn-icon" />
      ) : null}
      <small className="rmab-btn-label">{action.label}</small>
    </button>
  );

  return (
    <>
      {spacer}
      <Portal.Root>
        <div className={`results-mobile-action-bar${bpBar}`} role="toolbar" aria-label={ariaLabel}>
          {actions.map(renderBarButton)}
          {primary ? (
            <button
              type="button"
              className={`rmab-btn rmab-btn--primary${
                primary.active || primary.busy ? ' is-active' : ''
              }`}
              onClick={primary.onClick}
              disabled={primary.disabled}
              aria-pressed={primary.active || undefined}
              aria-busy={primary.busy || undefined}
              aria-label={accessibleLabel(primary.label, primary.busy)}
            >
              {primary.icon ? (
                <img src={primary.icon} alt="" aria-hidden="true" className="rmab-btn-icon" />
              ) : null}
              <small className="rmab-btn-label">{primary.label}</small>
            </button>
          ) : null}
          {hasMore ? (
            <button
              type="button"
              className={`rmab-btn rmab-more${moreBusy ? ' is-active' : ''}`}
              onClick={() => setSheetOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={isSheetOpen}
              aria-busy={moreBusy || undefined}
              aria-label={accessibleLabel(moreLabel, moreBusy)}
            >
              <span className="rmab-more-dots" aria-hidden="true">•••</span>
            </button>
          ) : null}
        </div>
      </Portal.Root>

      <MobileSheet isOpen={isSheetOpen} onClose={() => setSheetOpen(false)} title={sheetTitle}>
        <ul className="mobile-sheet-action-list">
          {moreActions.map((action) => (
            <li key={action.key}>
              <button
                type="button"
                className="mobile-sheet-action-item"
                onClick={() => {
                  action.onClick?.();
                  setSheetOpen(false);
                }}
                disabled={action.disabled}
              >
                {action.icon ? (
                  <img
                    src={action.icon}
                    alt=""
                    aria-hidden="true"
                    className="mobile-sheet-action-item-icon"
                  />
                ) : null}
                <span>{action.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </MobileSheet>
    </>
  );
};

ResultsMobileActionBar.propTypes = {
  /** Inline secondary actions (icon + label), left of the primary CTA. */
  actions: PropTypes.arrayOf(actionShape),
  /** The emphasized, filled primary CTA (per surface: Save / Download / Send feedback). */
  primary: PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    /** Renders the CTA in a "done"/selected state (e.g. search already saved). */
    active: PropTypes.bool,
    /** See `busy` on the actions. */
    busy: PropTypes.bool
  }),
  /** Long-tail actions shown in the "More (⋯)" bottom sheet. */
  moreActions: PropTypes.arrayOf(actionShape),
  /** An action hosted in the More sheet is still running (see `busy` on the actions). */
  moreBusy: PropTypes.bool,
  moreLabel: PropTypes.string,
  sheetTitle: PropTypes.string,
  ariaLabel: PropTypes.string,
  /** Desktop hide breakpoint: 'to-1200' (Projects/Team) or 'to-833' (Search).
      Hides the bar + spacer above the surface's mobile switch point via CSS. */
  breakpoint: PropTypes.oneOf(['to-1200', 'to-833'])
};

export default ResultsMobileActionBar;
